import React, { useState, useMemo } from "react";
import LinearSelector from "../token/LinearSelector";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import CustomTooltip from "../token/Tooltip";
import { StatBox } from "../token/StatsBox";
import type { SaleData } from "../token/StatsBox";
interface DashboardProps {
  data: SaleData[];
}
const DashboardRevenues: React.FC<DashboardProps> = ({ data }) => {
  const [selectedView, setSelectedView] = useState("Charte");

  const processedData = data;

  const today = new Date();
  const currentYear = today.getFullYear();
  const availableYears = useMemo(
    () =>
      Array.from(
        new Set(
          processedData
            .map((p) => p.paymentDate?.getFullYear())
            .filter((y): y is number => y !== undefined && y !== null)
        )
      ).sort((a, b) => b - a),
    [processedData]
  );
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // ── Indicateurs pour 2024 ─────────────────────────────────────────────
  const totalRevenue2024 = processedData
    .filter((p) => p.paymentDate?.getFullYear() === 2024)
    .reduce((sum, p) => sum + p.amount, 0);

  const revenueSep2024 = processedData
    .filter(
      (p) =>
        p.paymentDate?.getFullYear() === 2024 && p.paymentDate.getMonth() === 8
    )
    .reduce((sum, p) => sum + p.amount, 0);

  // ── Projets en attente de paiement ────────────────────────────────────
  // Fin de projet passée, et pas (encore) payés
  const pendingPayment = processedData
    .filter(
      (p) =>
        p.endDate instanceof Date &&
        p.endDate < today &&
        (!p.paymentDate || p.paymentDate > today)
    )
    .reduce((sum, p) => sum + p.amount, 0);

  // ── Projets en cours de production ────────────────────────────────────
  // Début présent, pas de date de fin
  const enCoursProduction = processedData
    .filter((p) => p.startDate instanceof Date && !p.endDate)
    .reduce((sum, p) => sum + p.amount, 0);

  // ── Données mensuelles pour le graphique ──────────────────────────────
  const monthLabels = [
    "Janv.",
    "Fév.",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Sept.",
    "Oct.",
    "Nov.",
    "Déc.",
  ];
  const monthlyRevenue = monthLabels.map((label, idx) => ({
    month: label,
    revenue: processedData
      .filter(
        (p) =>
          p.paymentDate?.getFullYear() === selectedYear &&
          p.paymentDate.getMonth() === idx
      )
      .reduce((sum, p) => sum + p.amount, 0),
  }));

  // ── Chiffre d'affaire du mois précédent ────────────────────────────────
  const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevYear = prevMonthDate.getFullYear();
  const prevMonthIndex = prevMonthDate.getMonth();
  const previousMonthLabel = monthLabels[prevMonthIndex];
  const revenuePrevMonth = processedData
    .filter(
      (p) =>
        p.paymentDate?.getFullYear() === prevYear &&
        p.paymentDate.getMonth() === prevMonthIndex
    )
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="flex flex-col gap-8 w-full overflow-auto">
      <div className="grid grid-cols-4 gap-4">
        <StatBox
          label="Chiffre d’affaire - 2024"
          value={totalRevenue2024.toLocaleString("fr-FR", {
            style: "currency",
            currency: "EUR",
          })}
        />
        <StatBox
          label={`Chiffre d’affaire - ${previousMonthLabel} ${prevYear}`}
          value={revenuePrevMonth.toLocaleString("fr-FR", {
            style: "currency",
            currency: "EUR",
          })}
        />
        <StatBox
          label="En attente de paiement"
          value={pendingPayment.toLocaleString("fr-FR", {
            style: "currency",
            currency: "EUR",
          })}
        />
        <StatBox
          label="En cours de production"
          value={enCoursProduction.toLocaleString("fr-FR", {
            style: "currency",
            currency: "EUR",
          })}
        />
      </div>

      {/* Graphique */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <select
            className="border rounded p-2"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <div className="w-min">
            <LinearSelector
              options={["Graphique", "Charte"]}
              selected={selectedView}
              setSelected={setSelectedView}
            />
          </div>
        </div>

        {selectedView === "Charte" && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyRevenue}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F1F1F1"
                vertical={false}
              />
              <XAxis dataKey="month" />
              <YAxis
                tickFormatter={(value) => `${value.toLocaleString("fr-FR")} €`}
              />
              <Tooltip content={CustomTooltip} />
              <Line
                type="linear"
                dataKey="revenue"
                stroke="#000"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {selectedView === "Graphique" && (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyRevenue}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F1F1F1"
                vertical={false}
              />
              <XAxis dataKey="month" />
              <YAxis
                tickFormatter={(value) => `${value.toLocaleString("fr-FR")} €`}
              />
              <Tooltip content={CustomTooltip} />
              <Bar
                dataKey="revenue"
                fill="#99C3FF"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default DashboardRevenues;
